import { useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { EVENT_CANDIDATES, EventCandidate } from './mockData'
import { SYSTEM_EVENT_TYPES } from './edaMockData'
import { useAiFilters } from './AiFilterContext'

/** Same shape as Events entity / POST /events/save/add body key `events` */
export type SystemEventPayload = {
  company: number
  company_site: number
  event_type: number
  name: string
  description: string
  date: string
  start_time: string
  end_time: string
  all_day: boolean
  frequency: 'one_time' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom'
  status: 'created' | 'completed' | 'cancelled'
  parent_event: number
}

type ImportedEvent = SystemEventPayload & {
  id: number
  event_type_name: string
  company_name: string
  site_name: string
  source_candidate_id: string
}

const mapSuggestedType = (suggested: string) => {
  const key = suggested.toLowerCase()
  if (key.includes('concert')) return SYSTEM_EVENT_TYPES.find((t) => t.name === 'Concert')!
  if (key.includes('rugby')) return SYSTEM_EVENT_TYPES.find((t) => t.name === 'Rugby')!
  if (key.includes('other')) return SYSTEM_EVENT_TYPES.find((t) => t.name === 'Other Football')!
  return SYSTEM_EVENT_TYPES.find((t) => t.name === 'Match')!
}

const buildPayload = (
  row: EventCandidate,
  companyId: number,
  siteId: number
): SystemEventPayload => {
  const eventType = mapSuggestedType(row.suggestedType)
  const kickoff = row.kickoff || '15:00'
  const [hh, mm] = kickoff.split(':').map(Number)
  const startH = Math.max(0, hh + eventType.start_hour_offset)
  const endH = Math.min(23, hh + eventType.end_hour_offset)
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)

  return {
    company: companyId,
    company_site: siteId,
    event_type: eventType.id,
    name: row.name,
    description: [
      row.details,
      `Source: ${row.source}`,
      row.attendance != null ? `Attendance ~${row.attendance}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    date: row.date,
    start_time: `${pad(startH)}:${pad(mm || 0)}:00`,
    end_time: `${pad(endH)}:${pad(mm || 0)}:00`,
    all_day: false,
    frequency: 'one_time',
    status: 'created',
    parent_event: 0,
  }
}

const confidenceBadge = (c: EventCandidate['confidence']) => {
  if (c === 'high') return 'badge-light-success'
  if (c === 'medium') return 'badge-light-warning'
  return 'badge-light-danger'
}

const statusBadge = (s: EventCandidate['status']) => {
  switch (s) {
    case 'pending':
      return 'badge-light-primary'
    case 'approved':
      return 'badge-light-success'
    case 'auto_applied':
      return 'badge-light-info'
    case 'rejected':
      return 'badge-light-danger'
    default:
      return 'badge-light'
  }
}

const AiAutoEventsView = () => {
  const { company, site } = useAiFilters()
  const companyId = Number(company.value)
  const siteId = Number(site.value)

  const [rows, setRows] = useState(EVENT_CANDIDATES)
  const [imported, setImported] = useState<ImportedEvent[]>(() => {
    const auto = EVENT_CANDIDATES.find((r) => r.status === 'auto_applied')
    if (!auto) return []
    const payload = buildPayload(auto, companyId, siteId)
    const et = mapSuggestedType(auto.suggestedType)
    return [
      {
        ...payload,
        id: 9001,
        event_type_name: et.name,
        company_name: company.label,
        site_name: site.label,
        source_candidate_id: auto.id,
      },
    ]
  })
  const [preview, setPreview] = useState<{
    candidate: EventCandidate
    payload: SystemEventPayload
  } | null>(null)
  const [nextId, setNextId] = useState(9100)

  const pending = rows.filter((r) => r.status === 'pending')

  const postBodyExample = useMemo(() => {
    if (!preview) return null
    return { events: preview.payload }
  }, [preview])

  const openPreview = (row: EventCandidate) => {
    setPreview({ candidate: row, payload: buildPayload(row, companyId, siteId) })
  }

  const approveFromPreview = () => {
    if (!preview) return
    const et = mapSuggestedType(preview.candidate.suggestedType)
    const id = nextId
    setNextId((n) => n + 1)
    setImported((prev) => [
      {
        ...preview.payload,
        id,
        event_type_name: et.name,
        company_name: company.label,
        site_name: site.label,
        source_candidate_id: preview.candidate.id,
      },
      ...prev,
    ])
    setRows((prev) =>
      prev.map((r) => (r.id === preview.candidate.id ? { ...r, status: 'approved' } : r))
    )
    setPreview(null)
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Event imported',
      showConfirmButton: false,
      timer: 1600,
    })
  }

  const reject = (id: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'rejected' } : r)))
  }

  return (
    <>
      <div className='row g-4 mb-5'>
        <div className='col-6 col-md-3'>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='fs-7 text-gray-500 fw-semibold'>Pending</div>
              <div className='fs-2hx fw-bold text-primary'>{pending.length}</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-3'>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='fs-7 text-gray-500 fw-semibold'>Imported</div>
              <div className='fs-2hx fw-bold text-success'>{imported.length}</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-3'>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='fs-7 text-gray-500 fw-semibold'>Company</div>
              <div className='fs-5 fw-bold'>{company.label}</div>
            </div>
          </div>
        </div>
        <div className='col-6 col-md-3'>
          <div className='card border-0 shadow-sm h-100'>
            <div className='card-body'>
              <div className='fs-7 text-gray-500 fw-semibold'>Site</div>
              <div className='fs-5 fw-bold'>{site.label}</div>
            </div>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-body d-flex flex-wrap align-items-center justify-content-between gap-3 py-4'>
          <h3 className='mb-0 fw-bold fs-4'>Fixture sync</h3>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() =>
              Swal.fire({
                icon: 'success',
                title: 'Synced',
                timer: 1200,
                showConfirmButton: false,
              })
            }
          >
            Sync now
          </button>
        </div>
      </div>

      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bold fs-4'>Candidates</h3>
        </div>
        <div className='card-body py-3'>
          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-semibold text-muted'>
                  <th>Date</th>
                  <th>Suggested → Event Type</th>
                  <th>Name</th>
                  <th>Mapped times</th>
                  <th>Confidence</th>
                  <th>Status</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const payload = buildPayload(row, companyId, siteId)
                  const et = mapSuggestedType(row.suggestedType)
                  return (
                    <tr key={row.id}>
                      <td className='fw-semibold'>{row.date}</td>
                      <td>
                        <span className='badge badge-light me-1'>{row.suggestedType}</span>
                        <i className='fa fa-arrow-right text-muted mx-1 fs-8' />
                        <span className='badge badge-light-primary'>{et.name}</span>
                        <div className='fs-8 text-muted'>event_type id={et.id}</div>
                      </td>
                      <td>
                        <div className='fw-semibold'>{row.name}</div>
                        <div className='fs-8 text-muted'>{row.source}</div>
                      </td>
                      <td className='fs-7'>
                        {payload.start_time.slice(0, 5)} – {payload.end_time.slice(0, 5)}
                        <div className='text-muted fs-8'>
                          KO {row.kickoff || '—'} · offsets {et.start_hour_offset}/+
                          {et.end_hour_offset}h
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${confidenceBadge(row.confidence)}`}>
                          {row.confidence}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${statusBadge(row.status)}`}>
                          {row.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className='text-end'>
                        {row.status === 'pending' ? (
                          <div className='d-flex justify-content-end gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => openPreview(row)}
                            >
                              Review
                            </button>
                            <button
                              type='button'
                              className='btn btn-sm btn-light-danger'
                              onClick={() => reject(row.id)}
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className='text-muted fs-8'>—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className='card border-0 shadow-sm'>
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bold fs-4'>Imported events</h3>
        </div>
        <div className='card-body pt-0'>
          {imported.length === 0 ? (
            <p className='text-muted mb-0'>No imported events yet.</p>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-semibold text-muted'>
                    <th>ID</th>
                    <th>Company</th>
                    <th>Site</th>
                    <th>Event type</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Frequency</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {imported.map((ev) => (
                    <tr key={ev.id}>
                      <td>{ev.id}</td>
                      <td>{ev.company_name}</td>
                      <td>{ev.site_name}</td>
                      <td>
                        <span className='badge badge-light'>{ev.event_type_name}</span>
                      </td>
                      <td>
                        <div className='fw-semibold'>{ev.name}</div>
                        <div className='fs-8 text-muted text-truncate' style={{ maxWidth: 220 }}>
                          {ev.description}
                        </div>
                      </td>
                      <td>{ev.date}</td>
                      <td>{ev.start_time}</td>
                      <td>{ev.end_time}</td>
                      <td>
                        <span className='badge badge-light-info'>{ev.frequency}</span>
                      </td>
                      <td>
                        <span className='badge badge-light-success'>{ev.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={!!preview} onHide={() => setPreview(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>Import event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview && postBodyExample && (
            <>
              <pre
                className='bg-light rounded p-4 fs-8'
                style={{ maxHeight: 320, overflow: 'auto' }}
              >
                {JSON.stringify(postBodyExample, null, 2)}
              </pre>
              <div className='table-responsive mt-4'>
                <table className='table table-sm'>
                  <tbody>
                    <tr>
                      <th className='w-200px'>API</th>
                      <td>
                        <code>POST /events/save/add</code>
                      </td>
                    </tr>
                    <tr>
                      <th>Event type</th>
                      <td>
                        {mapSuggestedType(preview.candidate.suggestedType).name} (id{' '}
                        {preview.payload.event_type})
                      </td>
                    </tr>
                    <tr>
                      <th>Window</th>
                      <td>
                        {preview.payload.date} {preview.payload.start_time} →{' '}
                        {preview.payload.end_time}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={() => setPreview(null)}>
            Cancel
          </button>
          <button type='button' className='btn btn-success' onClick={approveFromPreview}>
            Approve &amp; import
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AiAutoEventsView
